import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta, initials } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MultiSelectField } from "@/components/MultiSelectField";
import { toast } from "sonner";
import { ChevronLeft, UserRound, Palette, Upload, Trash2, Lock, ExternalLink } from "lucide-react";

const MAX_IMG = 2.5 * 1024 * 1024;

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
const listToStr = (v) => (Array.isArray(v) ? v.join(", ") : (v || ""));
const strToList = (s) => String(s || "").split(",").map((x) => x.trim()).filter(Boolean).slice(0, 10);

export default function Profile() {
  const { user } = useAuth();
  const t = useT();
  usePageMeta(t("prof.meta_title"));
  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  return (
    <div data-testid="coach-profile-page">
      <Seo title={t("prof.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="profile-back">
        <ChevronLeft className="h-4 w-4" /> {t("prof.back")}
      </Link>
      <PageHeader eyebrow={t("prof.eyebrow")} title={t("prof.title")} subtitle={t("prof.subtitle")} />
      <Tabs defaultValue="profile">
        <TabsList className="mb-4 flex h-auto w-full flex-wrap justify-start gap-1 bg-secondary/60 p-1">
          <TabsTrigger value="profile" data-testid="prof-tab-profile"><UserRound className="mr-1.5 h-4 w-4" />{t("prof.tab_profile")}</TabsTrigger>
          <TabsTrigger value="branding" data-testid="prof-tab-branding"><Palette className="mr-1.5 h-4 w-4" />{t("prof.tab_branding")}</TabsTrigger>
        </TabsList>
        <TabsContent value="profile"><ProfileTab t={t} /></TabsContent>
        <TabsContent value="branding"><BrandingTab t={t} /></TabsContent>
      </Tabs>
    </div>
  );
}

function ProfileTab({ t }) {
  const { user, refreshUser } = useAuth();
  const p = user?.profile || {};
  const [opts, setOpts] = useState(null);
  const [form, setForm] = useState({
    name: user?.name || "",
    photo: p.photo || "",
    header_image: p.header_image || "",
    bio: p.bio || "",
    professions: Array.isArray(p.professions) ? p.professions : (p.profession ? [p.profession] : []),
    topics: Array.isArray(p.topics) ? p.topics : [],
    methods: Array.isArray(p.methods) ? p.methods : [],
    location: p.location || "",
    website: p.website || "",
    languages_spoken: listToStr(p.languages_spoken),
    years_experience: p.years_experience ?? "",
    target_audience: p.target_audience || "",
    visible: p.profile_visibility !== "private",
  });
  const [saving, setSaving] = useState(false);
  const photoRef = useRef(null);
  const headerRef = useRef(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    api.get("/coach/profile/options").then(({ data }) => setOpts(data)).catch(() => setOpts({ professions: [], methods: [], topics: [] }));
  }, []);

  const pick = async (e, key) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_IMG) { toast.error(t("prof.too_large")); return; }
    try { set({ [key]: await readAsDataUrl(file) }); } catch { toast.error(t("prof.save_failed")); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/profile", {
        name: form.name.trim(),
        photo: form.photo || null,
        header_image: form.header_image || null,
        bio: form.bio.trim(),
        professions: form.professions.slice(0, 5),
        topics: form.topics,
        methods: form.methods,
        location: form.location.trim(),
        website: form.website.trim(),
        languages_spoken: strToList(form.languages_spoken),
        years_experience: form.years_experience === "" ? null : parseInt(form.years_experience) || 0,
        target_audience: form.target_audience.trim(),
        profile_visibility: form.visible ? "public" : "private",
      });
      await refreshUser();
      toast.success(t("prof.saved"));
    } catch (err) { toast.error(apiError(err, t("prof.save_failed"))); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      {/* Images */}
      <div className="rounded-2xl border border-border bg-card p-5">
        {form.header_image ? (
          <div className="relative mb-4 overflow-hidden rounded-xl">
            <img src={form.header_image} alt="" className="aspect-[3/1] w-full object-cover" />
            <button onClick={() => set({ header_image: "" })} data-testid="prof-header-remove" className="absolute right-2 top-2 rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70"><Trash2 className="h-4 w-4" /></button>
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-4">
          <Avatar className="h-20 w-20">
            {form.photo && <AvatarImage src={form.photo} alt={form.name} />}
            <AvatarFallback className="text-xl">{initials(form.name)}</AvatarFallback>
          </Avatar>
          <div className="flex flex-wrap gap-2">
            <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e, "photo")} data-testid="prof-photo-input" />
            <input ref={headerRef} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e, "header_image")} data-testid="prof-header-input" />
            <Button variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={() => photoRef.current?.click()} data-testid="prof-photo-pick"><Upload className="h-4 w-4" /> {t("prof.photo")}</Button>
            {form.photo && <Button variant="outline" size="sm" className="gap-1.5 rounded-full text-rose-600" onClick={() => set({ photo: "" })} data-testid="prof-photo-remove"><Trash2 className="h-4 w-4" /> {t("prof.remove")}</Button>}
            <Button variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={() => headerRef.current?.click()} data-testid="prof-header-pick"><Upload className="h-4 w-4" /> {t("prof.header")}</Button>
          </div>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{t("prof.photo_hint")}</p>
      </div>

      {/* Fields */}
      <div className="grid gap-4 rounded-2xl border border-border bg-card p-5 sm:grid-cols-2">
        <F label={t("prof.name")}><Input value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="prof-name" /></F>
        <F label={t("prof.location")}><Input value={form.location} onChange={(e) => set({ location: e.target.value })} placeholder={t("prof.location_ph")} data-testid="prof-location" /></F>
        <F label={t("prof.bio")} full><Textarea className="min-h-[110px]" value={form.bio} onChange={(e) => set({ bio: e.target.value })} placeholder={t("prof.bio_ph")} data-testid="prof-bio" /></F>
        <div className="sm:col-span-2">
          <MultiSelectField label={t("prof.professions")} hint={t("prof.professions_hint")} max={5}
            options={opts?.professions || []} groups={opts?.professionGroups || {}}
            value={form.professions} onChange={(v) => set({ professions: v })}
            placeholder={t("prof.professions_ph")} searchPlaceholder={t("common.search")} emptyText={t("common.none_found")} testId="prof-professions" />
        </div>
        <div className="sm:col-span-2">
          <MultiSelectField label={t("prof.methods")} hint={t("prof.methods_hint")}
            options={opts?.methods || []} groups={opts?.methodGroups || {}}
            value={form.methods} onChange={(v) => set({ methods: v })}
            placeholder={t("prof.methods_ph")} searchPlaceholder={t("common.search")} emptyText={t("common.none_found")} testId="prof-methods" />
        </div>
        <div className="sm:col-span-2">
          <MultiSelectField label={t("prof.topics")} hint={t("prof.topics_hint")}
            options={opts?.topics || []} groups={opts?.topicGroups || {}}
            value={form.topics} onChange={(v) => set({ topics: v })}
            placeholder={t("prof.topics_ph")} searchPlaceholder={t("common.search")} emptyText={t("common.none_found")} testId="prof-topics" />
        </div>
        <F label={t("prof.languages")} hint={t("prof.languages_hint")}><Input value={form.languages_spoken} onChange={(e) => set({ languages_spoken: e.target.value })} data-testid="prof-languages" /></F>
        <F label={t("prof.experience")}><Input type="number" min="0" value={form.years_experience} onChange={(e) => set({ years_experience: e.target.value })} data-testid="prof-experience" /></F>
        <F label={t("prof.website")}><Input value={form.website} onChange={(e) => set({ website: e.target.value })} placeholder={t("prof.website_ph")} data-testid="prof-website" /></F>
        <F label={t("prof.audience")} full><Input value={form.target_audience} onChange={(e) => set({ target_audience: e.target.value })} placeholder={t("prof.audience_ph")} data-testid="prof-audience" /></F>
        <div className="sm:col-span-2 flex items-center justify-between rounded-xl border border-border p-3">
          <div>
            <p className="text-sm font-medium text-foreground">{t("prof.visibility_public")}</p>
            <p className="text-xs text-muted-foreground">{t("prof.visibility_hint")}</p>
          </div>
          <Switch checked={form.visible} onCheckedChange={(v) => set({ visible: v })} data-testid="prof-visibility" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button className="rounded-full" onClick={save} disabled={saving} data-testid="prof-save">{saving ? t("common.saving") : t("common.save")}</Button>
        {user?.id && (
          <a href={`/coaches/${user.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="prof-view-public">
            {t("prof.view_public")} <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </div>
  );
}

function BrandingTab({ t }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ logo_url: "", brand_color: "#6B8E6E", footer_text: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/coach/branding").then(({ data: d }) => {
      setData(d);
      const b = d?.branding || {};
      setForm({ logo_url: b.logo_url || "", brand_color: b.brand_color || "#6B8E6E", footer_text: b.footer_text || "" });
    }).catch(() => setData({ is_pro: false, branding: {} }));
  }, []);

  if (data === null) return <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}</div>;

  if (!data.is_pro) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="brand-locked">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700"><Lock className="h-6 w-6" /></div>
        <p className="mt-4 font-display text-lg text-foreground">{t("brand.locked_title")}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t("brand.locked_sub")}</p>
        <Button asChild className="mt-4 rounded-full"><Link to="/app/membership" data-testid="brand-upgrade">{t("brand.upgrade")}</Link></Button>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/coach/branding", {
        logo_url: form.logo_url.trim() || null,
        brand_color: form.brand_color || null,
        footer_text: form.footer_text.trim() || null,
      });
      toast.success(t("brand.saved"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    try { await api.delete("/coach/branding"); setForm({ logo_url: "", brand_color: "#6B8E6E", footer_text: "" }); toast.success(t("brand.reset_done")); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">{t("brand.subtitle")}</p>
      <div className="grid gap-4 rounded-2xl border border-border bg-card p-5">
        <F label={t("brand.logo")}><Input value={form.logo_url} onChange={(e) => setForm((f) => ({ ...f, logo_url: e.target.value }))} placeholder={t("brand.logo_ph")} data-testid="brand-logo" /></F>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("brand.color")}</label>
          <div className="flex items-center gap-3">
            <input type="color" value={form.brand_color} onChange={(e) => setForm((f) => ({ ...f, brand_color: e.target.value }))} className="h-10 w-14 cursor-pointer rounded-lg border border-border" data-testid="brand-color" />
            <Input value={form.brand_color} onChange={(e) => setForm((f) => ({ ...f, brand_color: e.target.value }))} className="max-w-[140px]" data-testid="brand-color-hex" />
          </div>
        </div>
        <F label={t("brand.footer")}><Textarea className="min-h-[70px]" maxLength={200} value={form.footer_text} onChange={(e) => setForm((f) => ({ ...f, footer_text: e.target.value }))} placeholder={t("brand.footer_ph")} data-testid="brand-footer" /></F>
      </div>
      <div className="flex gap-3">
        <Button className="rounded-full" onClick={save} disabled={saving} data-testid="brand-save">{saving ? t("common.saving") : t("common.save")}</Button>
        <Button variant="outline" className="rounded-full" onClick={reset} data-testid="brand-reset">{t("brand.reset")}</Button>
      </div>
    </div>
  );
}

function F({ label, hint, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
